// src/components/WordListBox.js
import { useEffect, useState, useRef, useCallback } from "react";
import './WordListBox.css';
import { API_URL } from "../config/api";
import {
  buildRegisterFilterQuery,
  isRegisterFilterActive,
} from "../constants/registerFilters";
import { DEFAULT_REGISTER_PREFERENCES } from "../constants/registerPreferences";
import {
  matchesRegisterSearch,
  normalizeRegisterSearch,
} from "../utils/registerWordListDisplay";
import WordListMetaBadge from "./WordListMetaBadge";
import "./RegisterSkeleton.css";

const REGISTER_LIST_SKELETON_WIDTHS = [
  "46%",
  "61%",
  "38%",
  "54%",
  "49%",
  "67%",
  "41%",
  "58%",
  "44%",
  "63%",
  "36%",
  "52%",
  "59%",
  "43%",
  "70%",
  "40%",
  "55%",
  "47%",
  "64%",
  "39%",
  "51%",
  "57%",
];

function WordListSkeleton({ showColorCode }) {
  return (
    <div
      className="word-listbox-shell word-listbox-shell--skeleton"
      aria-hidden="true"
    >
      <div
        className={`listbox custom-scroll${
          showColorCode ? "" : " listbox--no-color-code"
        }`}
      >
        {REGISTER_LIST_SKELETON_WIDTHS.map((width, index) => (
          <div key={index} className="word-list-item">
            <span className="word-list-item__label">
              <span className="register-skel register-skel--label" style={{ width }} />
            </span>
            <span className="word-list-meta-badge register-skel" />
          </div>
        ))}
      </div>
    </div>
  );
}

const SCROLL_LS_KEY = "vocabularium_register_scroll";

function readScrollMap() {
  try {
    const raw = localStorage.getItem(SCROLL_LS_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

function normalizeMotKey(value) {
  return String(value || "")
    .trim()
    .toLowerCase();
}

/** Complète noyauActif depuis /packs si l'API liste est incomplète ou périmée. */
async function enrichListWithNoyauActif(items) {
  if (!Array.isArray(items) || items.length === 0) return items || [];
  try {
    const res = await fetch(`${API_URL}/packs`);
    if (!res.ok) return items;
    const json = await res.json();
    const noyauSet = new Set(
      (json?.config?.noyauActif || []).map((mot) => normalizeMotKey(mot)).filter(Boolean)
    );
    if (noyauSet.size === 0) return items;
    return items.map((entry) => ({
      ...entry,
      noyauActif:
        entry?.noyauActif === true || noyauSet.has(normalizeMotKey(entry?.mot)),
    }));
  } catch {
    return items;
  }
}

export default function WordListBox({
  onSelectWord,
  selectedWord,
  onWordsLoaded,
  onCountChange,
  filter = "",
  activeFilter = "info",
  successEwssFilter = "all",
  registerFilters = null,
  registerPreferences = DEFAULT_REGISTER_PREFERENCES,
  refreshKey = 0,
  keyboardNavEnabled = true,
  active = true,
  filterSelectionRef,
  onFilteredEntriesChange,
  onLoadingChange,
}) {
  const [words, setWords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const itemRefs = useRef({});
  const listRef = useRef(null);
  const scrollPositionsRef = useRef(null);
  if (scrollPositionsRef.current === null) {
    scrollPositionsRef.current = readScrollMap();
  }
  const scrollStorageKeyRef = useRef("");
  const pendingRestoreRef = useRef(true);
  const persistTimerRef = useRef(null);
  const filteredWordsRef = useRef([]);
  const selectedWordRef = useRef(null);
  const scrollIntoViewOnHighlightRef = useRef(false);
  const loadedListFilterRef = useRef(activeFilter);
  const [scrollFades, setScrollFades] = useState({ top: false, bottom: false });
  const normalizedFilter = normalizeRegisterSearch(filter);
  const searchMode = registerPreferences?.searchMode ?? DEFAULT_REGISTER_PREFERENCES.searchMode;
  const listDisplayMode =
    registerPreferences?.listDisplayMode ?? DEFAULT_REGISTER_PREFERENCES.listDisplayMode;
  const showColorCode =
    registerPreferences?.showColorCode ?? DEFAULT_REGISTER_PREFERENCES.showColorCode;
  const filteredWords = normalizedFilter
    ? words.filter((entry) => matchesRegisterSearch(entry, normalizedFilter, searchMode))
    : words;
  const registerFilterKey =
    activeFilter === "info" && registerFilters
      ? buildRegisterFilterQuery(registerFilters)
      : "";
  const scrollStorageKey = `${activeFilter}:${successEwssFilter}:${registerFilterKey}:${normalizedFilter}:${searchMode}`;

  // La vue courante a changé (filtre / recherche) → armer la restauration pour cette entrée.
  if (scrollStorageKeyRef.current !== scrollStorageKey) {
    scrollStorageKeyRef.current = scrollStorageKey;
    pendingRestoreRef.current = true;
  }

  // Refs "vivantes" pour la restauration (évite les closures périmées / recréation d'effets).
  filteredWordsRef.current = filteredWords;
  selectedWordRef.current = selectedWord;

  const updateScrollFades = useCallback(() => {
    const el = listRef.current;
    if (!el) return;

    const { scrollTop, scrollHeight, clientHeight } = el;
    const threshold = 6;

    setScrollFades({
      top: scrollTop > threshold,
      bottom: scrollTop + clientHeight < scrollHeight - threshold,
    });
  }, []);

  // Écrit immédiatement la position de scroll courante dans localStorage
  // (persiste au-delà d'un rafraîchissement de page).
  const flushScroll = useCallback(() => {
    const el = listRef.current;
    if (el && el.clientHeight > 0) {
      scrollPositionsRef.current[scrollStorageKeyRef.current] = el.scrollTop;
    }
    try {
      localStorage.setItem(SCROLL_LS_KEY, JSON.stringify(scrollPositionsRef.current));
    } catch {
      // quota / mode privé : on ignore
    }
  }, []);

  // Garantit que le mot sélectionné est entièrement visible dans la liste ;
  // sinon on le recentre (le navigateur borne le scrollTop, donc 1er/dernier
  // mot se calent en haut/bas). Renvoie true si un ajustement a eu lieu.
  const ensureSelectedVisible = useCallback(() => {
    const el = listRef.current;
    if (!el || el.clientHeight === 0) return false;
    const list = filteredWordsRef.current;
    const idx = list.findIndex((entry) => entry.mot === selectedWordRef.current);
    const item = idx >= 0 ? itemRefs.current[idx] : null;
    if (!item) return false;
    const elRect = el.getBoundingClientRect();
    const itemRect = item.getBoundingClientRect();
    const fullyVisible = itemRect.top >= elRect.top && itemRect.bottom <= elRect.bottom;
    if (fullyVisible) return false;
    // Les rects sont en pixels écran (cadre éventuellement mis à l'échelle) ;
    // on convertit en pixels de scroll via le rapport hauteur écran / clientHeight.
    const ratio = elRect.height > 0 ? el.clientHeight / elRect.height : 1;
    const delta =
      (itemRect.top - elRect.top - elRect.height / 2 + itemRect.height / 2) * ratio;
    el.scrollTop += delta;
    return true;
  }, []);

  // Restaure la position de scroll EXACTE mémorisée pour la vue courante, puis
  // garantit que le mot sélectionné est visible (s'il a été scrollé hors vue, on le
  // ramène au centre). À faire dès que la liste est visible et son contenu rendu.
  const tryRestoreScroll = useCallback(() => {
    if (!pendingRestoreRef.current) return;
    const el = listRef.current;
    if (!el || el.clientHeight === 0) return; // panneau encore masqué (display:none)

    const list = filteredWordsRef.current;
    const idx = list.findIndex((entry) => entry.mot === selectedWordRef.current);
    // Un mot est sélectionné mais ses éléments ne sont pas encore montés → réessai.
    if (idx >= 0 && !itemRefs.current[idx]) return;

    // 1) Restaurer la position exacte mémorisée.
    const saved = scrollPositionsRef.current[scrollStorageKeyRef.current];
    if (typeof saved === "number" && saved > 0) {
      el.scrollTop = saved;
    }

    // 2) Garantir la visibilité du mot sélectionné.
    ensureSelectedVisible();

    pendingRestoreRef.current = false;
    updateScrollFades();
  }, [ensureSelectedVisible, updateScrollFades]);

  const handleScroll = useCallback(() => {
    const el = listRef.current;
    if (el) {
      scrollPositionsRef.current[scrollStorageKey] = el.scrollTop;
      if (persistTimerRef.current) clearTimeout(persistTimerRef.current);
      persistTimerRef.current = setTimeout(() => {
        try {
          localStorage.setItem(SCROLL_LS_KEY, JSON.stringify(scrollPositionsRef.current));
        } catch {
          // ignore
        }
      }, 120);
    }
    updateScrollFades();
  }, [scrollStorageKey, updateScrollFades]);

  useEffect(() => {
    onFilteredEntriesChange?.(filteredWords);
  }, [filteredWords, onFilteredEntriesChange]);

  useEffect(() => {
    onLoadingChange?.(loading);
  }, [loading, onLoadingChange]);

  useEffect(() => {
    loadedListFilterRef.current = null;

    const fetchWords = async () => {
      try {
        setLoading(true);
        setError(null);
        let data;
        let countPayload = null;

        const gameFilters = ["neverPlayed", "success", "failures", "lastGame", "favorites", "priority"];

        if (activeFilter === "info") {
          let url = `${API_URL}/mots-liste-detail`;
          if (registerFilters && isRegisterFilterActive(registerFilters)) {
            const query = buildRegisterFilterQuery(registerFilters);
            if (query) url += `?${query}`;
          }
          const res = await fetch(url);
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          data = await enrichListWithNoyauActif(await res.json());
        } else if (gameFilters.includes(activeFilter)) {
          let url = `${API_URL}/mots-liste-detail?filter=${encodeURIComponent(activeFilter)}`;
          if (activeFilter === "success" && successEwssFilter && successEwssFilter !== "all") {
            url += `&ewss=${encodeURIComponent(successEwssFilter)}`;
          }
          const res = await fetch(url);
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          const json = await res.json();
          data = await enrichListWithNoyauActif(json.items || []);
          countPayload = {
            count: json.count ?? data.length,
            totalWords: json.totalWords ?? data.length,
            percent: json.percent ?? 0,
            lastSession: json.lastSession ?? null,
            wordInsights: json.wordInsights ?? [],
          };
        } else {
          data = [];
        }

        setWords(data);
        loadedListFilterRef.current = activeFilter;
        pendingRestoreRef.current = true;

        const mots = data.map((entry) => entry.mot);
        const savedWord = filterSelectionRef?.current?.[activeFilter];
        const wordToSelect =
          savedWord && mots.includes(savedWord) ? savedWord : mots[0] || null;

        if (wordToSelect) {
          onSelectWord(wordToSelect, "left");
        } else if (mots.length === 0) {
          onSelectWord(null, null);
        }

        if (onCountChange) {
          if (countPayload) {
            onCountChange(countPayload);
          } else {
            onCountChange(data.length);
          }
        }

        if (onWordsLoaded && data.length > 0) {
          onWordsLoaded(mots);
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchWords();
  }, [activeFilter, refreshKey, successEwssFilter, registerFilters]);

  // Après chaque (re)chargement / changement de vue : restaurer la position exacte
  // une fois le contenu rendu (plusieurs tentatives via rAF le temps que la liste
  // atteigne sa hauteur définitive).
  useEffect(() => {
    if (loading) return;
    let frame1;
    let frame2;
    frame1 = requestAnimationFrame(() => {
      tryRestoreScroll();
      frame2 = requestAnimationFrame(() => tryRestoreScroll());
    });
    return () => {
      cancelAnimationFrame(frame1);
      if (frame2) cancelAnimationFrame(frame2);
    };
  }, [loading, filteredWords, scrollStorageKey, tryRestoreScroll]);

  // Persister la position avant un rafraîchissement / fermeture d'onglet, et au démontage.
  useEffect(() => {
    const onBeforeUnload = () => flushScroll();
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", onBeforeUnload);
      flushScroll();
    };
  }, [flushScroll]);

  // Synchroniser le surlignage avec le mot sélectionné (clic ou flèches)
  useEffect(() => {
    const idx = filteredWords.findIndex((entry) => entry.mot === selectedWord);
    if (idx >= 0) setHighlightedIndex(idx);
  }, [selectedWord, filteredWords]);

  // Retour/sortie du registre : le panneau gauche passe de display:none à visible,
  // ce qui remet scrollTop à 0. On arme la restauration exacte au retour, et on
  // persiste la position en quittant.
  const prevActiveRef = useRef(active);
  useEffect(() => {
    const wasActive = prevActiveRef.current;
    prevActiveRef.current = active;
    if (active && !wasActive) {
      pendingRestoreRef.current = true;
      requestAnimationFrame(() => tryRestoreScroll());
    } else if (!active && wasActive) {
      flushScroll();
    }
  }, [active, tryRestoreScroll, flushScroll]);

  // Désélectionner si la liste filtrée est vide (recherche ou filtres)
  useEffect(() => {
    if (loading) return;
    if (filteredWords.length === 0 && selectedWord) {
      onSelectWord(null, null);
    }
  }, [filteredWords.length, selectedWord, loading, onSelectWord]);

  // Défiler uniquement lors de la navigation clavier (pas au changement de filtre)
  useEffect(() => {
    if (!scrollIntoViewOnHighlightRef.current) return;
    scrollIntoViewOnHighlightRef.current = false;
    itemRefs.current[highlightedIndex]?.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }, [highlightedIndex]);

  const navigateWord = useCallback(
    (direction) => {
      if (!filteredWords.length) return;

      scrollIntoViewOnHighlightRef.current = true;

      setHighlightedIndex((prevHighlight) => {
        const currentIndex = filteredWords.findIndex((entry) => entry.mot === selectedWord);
        const baseIndex = currentIndex >= 0 ? currentIndex : prevHighlight;
        const nextIndex =
          direction === "down"
            ? (baseIndex + 1) % filteredWords.length
            : (baseIndex - 1 + filteredWords.length) % filteredWords.length;

        onSelectWord(filteredWords[nextIndex].mot, "left", { userInitiated: true });
        return nextIndex;
      });
    },
    [filteredWords, selectedWord, onSelectWord]
  );

  useEffect(() => {
    if (!keyboardNavEnabled) return;

    const handleGlobalKeyDown = (e) => {
      if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;

      const tag = e.target?.tagName?.toLowerCase();
      if (tag === "input" || tag === "textarea" || e.target?.isContentEditable) return;
      if (!filteredWords.length) return;

      e.preventDefault();
      navigateWord(e.key === "ArrowDown" ? "down" : "up");
    };

    window.addEventListener("keydown", handleGlobalKeyDown);
    return () => window.removeEventListener("keydown", handleGlobalKeyDown);
  }, [keyboardNavEnabled, filteredWords.length, navigateWord]);

  // Quand le filtre change : garder le mot courant s'il reste visible, sinon le 1er résultat
  useEffect(() => {
    if (filteredWords.length === 0) return;

    const currentIndex = filteredWords.findIndex((entry) => entry.mot === selectedWord);
    if (currentIndex >= 0) {
      setHighlightedIndex(currentIndex);
      return;
    }

    setHighlightedIndex(0);
    onSelectWord(filteredWords[0].mot, "left");
  }, [normalizedFilter]);

  useEffect(() => {
    updateScrollFades();
  }, [filteredWords, words, loading, updateScrollFades]);

  // Redimensionnement de la fenêtre / passage cadre naturel ↔ mis à l'échelle :
  // la hauteur de la liste change. Si elle réapparaît (0 → h, le navigateur a remis
  // scrollTop à 0) on rejoue la restauration exacte ; sinon on s'assure simplement
  // que le mot sélectionné reste visible, puis on mémorise la nouvelle position.
  useEffect(() => {
    const el = listRef.current;
    if (!el) return;
    let lastHeight = el.clientHeight;

    const observer = new ResizeObserver(() => {
      const height = el.clientHeight;
      const reappeared = lastHeight === 0 && height > 0;
      const resized = height !== lastHeight;
      lastHeight = height;

      if (reappeared) pendingRestoreRef.current = true;
      if (pendingRestoreRef.current) {
        tryRestoreScroll();
      } else if (resized && height > 0) {
        if (ensureSelectedVisible()) flushScroll();
      }
      updateScrollFades();
    });
    observer.observe(el);
    return () => observer.disconnect();
    // `loading` / `error` / `words.length` : la liste n'existe pas pendant le squelette
    // ou les états vides ; on (ré)attache l'observateur quand elle est montée.
  }, [updateScrollFades, tryRestoreScroll, ensureSelectedVisible, flushScroll, loading, error, words.length]);

  if (loading) return <WordListSkeleton showColorCode={showColorCode} />;
  if (error) return <div className="word-listbox-status">Erreur : {error}</div>;
  if (!words.length)
    return (
      <div className="word-listbox-status">
        {activeFilter === "favorites"
          ? "Aucun mot favori pour l'instant"
          : activeFilter === "priority"
          ? "Aucun mot prioritaire pour l'instant"
          : activeFilter === "neverPlayed"
          ? "Tous les mots ont déjà été joués"
          : activeFilter === "success"
          ? successEwssFilter === "withEwss"
            ? "Aucun mot restitué avec un score EWSS"
            : successEwssFilter === "withoutEwss"
            ? "Aucun mot restitué sans score EWSS"
            : "Aucun mot correctement restitué à la dernière apparition"
          : activeFilter === "failures"
          ? "Aucun mot en échec à la dernière apparition"
          : activeFilter === "lastGame"
          ? "Vous n'avez joué aucune partie pour le moment"
          : activeFilter === "info" && isRegisterFilterActive(registerFilters)
          ? "Aucun mot ne correspond à ces filtres"
          : "Aucun mot trouvé"}
      </div>
    );

  return (
    <div
      className={[
        "word-listbox-shell",
        scrollFades.top && "word-listbox-shell--fade-top",
        scrollFades.bottom && "word-listbox-shell--fade-bottom",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <div
        ref={listRef}
        className={`listbox custom-scroll${showColorCode ? "" : " listbox--no-color-code"}`}
        onScroll={handleScroll}
      >
      {filteredWords.length === 0 && (
        <div style={{ padding: "6px 10px", fontStyle: "italic", opacity: 0.6 }}>
          Aucun mot ne correspond
        </div>
      )}
      {filteredWords.map((entry, index) => {
        const mot = entry.mot;
        const isSelected = mot === selectedWord;
        const isHighlighted = index === highlightedIndex;

        return (
          <div
            key={mot}
            ref={(el) => {
              itemRefs.current[index] = el;
            }}
            className={`word-list-item${isSelected ? " word-list-item--selected" : ""}${isHighlighted && !isSelected ? " word-list-item--highlighted" : ""}`}
            onClick={() => onSelectWord(mot, "left", { userInitiated: true })}
          >
            <span className="word-list-item__label">{mot}</span>
            <WordListMetaBadge entry={entry} mode={listDisplayMode} />
          </div>
        );
      })}
      </div>
    </div>
  );
}
